/* SKRT shared gift coordinator. Public spend + recovery = ONE free PAIR.
 * Recovery input is an UNTRUSTED PREVIEW hint, not checkout authorization.
 * PRODUCTION since 2026-09-11 (mode "production" in snippets/skrt-recovery-config.liquid).
 * No discounts, purchased items, checkout navigation, or analytics-provider calls here.
 */
(function (root) {
  'use strict';

  /** Pure, one-step planner. Re-read the cart after every line mutation. */
  function planGift(cart, cfg, state) {
    const gifts = cart.items.filter((item) => Number(item.variant_id) === cfg.variantId);
    const giftQuantity = gifts.reduce((sum, item) => sum + Number(item.quantity), 0);
    const paid = Number(cart.total_price) > 0 && cart.items.some((item) =>
      Number(item.variant_id) !== cfg.variantId &&
      item.gift_card !== true && item.requires_shipping !== false &&
      Number(item.final_line_price) > 0
    );
    const publicEligible = paid && Number(cart.total_price) >= cfg.threshold;
    const recoveryEligible = paid && state.recovery === true;
    const eligible = publicEligible || recoveryEligible;
    let action = null;
    if ((!eligible || !cfg.available) && gifts.length) {
      action = { type: 'change', key: gifts[gifts.length - 1].key, quantity: 0 };
    } else if (eligible && cfg.available && gifts.length > 1) {
      action = { type: 'change', key: gifts[gifts.length - 1].key, quantity: 0 };
    } else if (eligible && cfg.available && gifts.length && gifts[0].quantity !== 1) {
      action = { type: 'change', key: gifts[0].key, quantity: 1 };
    } else if (eligible && cfg.available && !gifts.length && !state.dismissed) {
      action = { type: 'add', quantity: 1 };
    }
    return { eligible, publicEligible, recoveryEligible, giftQuantity, action };
  }

  /** Accept only the preview offer and bounded, non-PII reporting fields. */
  function parseClaim(params, enabled) {
    if (!enabled || params.get('skrt_recovery') !== 'earrings-preview-v1') return null;
    const flow = params.get('skrt_recovery_flow');
    const stage = Number(params.get('skrt_recovery_stage'));
    if (!['cart', 'checkout'].includes(flow) || ![2, 3, 4].includes(stage)) return null;
    return { offer: 'earrings-preview-v1', flow, stage };
  }

  function storedClaim(attributes, enabled) {
    return parseClaim(new URLSearchParams({
      skrt_recovery: attributes.skrt_recovery_offer || '',
      skrt_recovery_flow: attributes.skrt_recovery_flow || '',
      skrt_recovery_stage: attributes.skrt_recovery_stage || ''
    }), enabled && attributes.skrt_recovery_preview === '1');
  }

  const policy = { planGift, parseClaim, storedClaim };
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = policy;
    return;
  }
  if (!root || root.__skrtGwp) return;
  root.__skrtGwp = true;

  const doc = root.document;
  const storageKey = 'skrt_gwp_v2';
  const pendingKey = 'skrt_gwp_pending_preview';
  let running = null;
  let queued = false;
  let timer = null;
  let lastCart = null;
  let pendingClaim = null;
  let manualDismissal = null;
  let recoveryConfig = {};
  let lastSignal = '';

  try {
    const configNode = doc.querySelector('[data-skrt-recovery-config]');
    recoveryConfig = configNode ? JSON.parse(configNode.textContent) : {};
  } catch (_) { /* Invalid config fails closed for recovery. */ }

  function storageRead(key) {
    try { return JSON.parse(root.sessionStorage.getItem(key) || 'null'); }
    catch (_) { return null; }
  }
  function storageWrite(key, value) {
    try {
      if (value === null) root.sessionStorage.removeItem(key);
      else root.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (_) { /* Cart attributes remain available if browser storage is blocked. */ }
  }

  // These flags are intentionally preview-only and never prove email entitlement.
  const previewEnabled = recoveryConfig.mode === 'preview' || recoveryConfig.mode === 'production';
  const entryURL = new URL(root.location.href);
  pendingClaim = parseClaim(entryURL.searchParams, previewEnabled);
  if (pendingClaim) {
    storageWrite(pendingKey, pendingClaim);
    for (const key of ['skrt_recovery', 'skrt_recovery_flow', 'skrt_recovery_stage']) {
      entryURL.searchParams.delete(key);
    }
    root.history.replaceState(root.history.state, '', entryURL.href);
  } else if (previewEnabled) {
    const pending = storageRead(pendingKey);
    if (pending) {
      pendingClaim = parseClaim(new URLSearchParams({
        skrt_recovery: pending.offer,
        skrt_recovery_flow: pending.flow,
        skrt_recovery_stage: String(pending.stage)
      }), true);
    }
  }

  function configuration() {
    const element = doc.querySelector('[data-skrt-gwp]');
    if (!element) return null; // Concept lazily renders the drawer.
    const variantId = Number(element.getAttribute('data-gift-variant-id'));
    const threshold = Number(element.getAttribute('data-threshold-cents'));
    if (!Number.isSafeInteger(variantId) || variantId <= 0 || !(threshold > 0)) return null;
    // A catalog price change must never make this coordinator remove a paid item.
    if (element.getAttribute('data-gift-is-free') !== 'true') return null;
    return {
      variantId, threshold,
      available: element.getAttribute('data-gift-available') === 'true'
    };
  }

  function route(path) {
    const prefix = root.Shopify && root.Shopify.routes ? root.Shopify.routes.root : '/';
    const url = new URL(prefix + path, root.location.origin);
    if (url.origin !== root.location.origin) throw new Error('Unexpected cart origin');
    return url.href;
  }

  async function request(path, body) {
    const controller = new AbortController();
    const timeout = root.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await root.fetch(route(path), {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('Gift cart request failed: ' + response.status);
      return await response.json();
    } finally { root.clearTimeout(timeout); }
  }

  function localState(cart) {
    const saved = storageRead(storageKey);
    const result = saved && saved.cartToken === cart.token ? saved : {
      cartToken: cart.token, dismissed: false
    };
    // This is a functional cart preference, not an entitlement.
    const preference = (cart.attributes || {}).skrt_gift_opt_out;
    if (preference === '1') result.dismissed = true;
    if (preference === '0') result.dismissed = false;
    if (manualDismissal !== null) result.dismissed = manualDismissal;
    return result;
  }

  function message(node, name) {
    return node.getAttribute('data-gift-' + name) || '';
  }

  function render(cart, cfg, state, failed) {
    const result = planGift(cart, cfg, state);
    for (const node of doc.querySelectorAll('[data-skrt-gwp]')) {
      if (Number(node.getAttribute('data-gift-variant-id')) !== cfg.variantId) continue;
      const text = node.querySelector('.skrt-gwp__msg');
      const badge = node.querySelector('.skrt-gwp__badge');
      const bar = node.querySelector('.skrt-gwp__bar');
      const restore = node.querySelector('[data-skrt-gift-restore]');
      const present = result.giftQuantity === 1 && result.eligible && cfg.available && !failed;
      node.classList.toggle('is-unlocked', present);
      node.setAttribute('data-gift-in-cart', present ? 'true' : 'false');
      if (badge) badge.textContent = message(node, state.recovery ? 'recovery-label' : 'label');
      if (restore) {
        restore.hidden = !failed && (!result.eligible || !cfg.available || present || !state.dismissed);
        restore.textContent = message(node, failed ? 'retry' : 'restore');
      }
      let copy = '';
      if (failed) copy = message(node, 'error');
      else if (!cfg.available) copy = message(node, 'unavailable');
      else if (present) copy = message(node, 'confirmed');
      else if (result.eligible && state.dismissed) copy = message(node, 'removed');
      else if (state.recovery && !result.eligible) copy = message(node, 'needs-item');
      else if (result.eligible) copy = message(node, 'adding');
      if (copy && text) text.textContent = copy;
      // Below threshold, let the refreshed native Liquid module display its localized amount.
      if (bar) {
        const progress = result.eligible ? 100 : Math.min(100, Math.max(0, cart.total_price / cfg.threshold * 100));
        bar.style.setProperty('--progress', progress + '%');
        bar.setAttribute('aria-valuenow', String(Math.round(progress)));
      }
    }
    return result;
  }

  async function patchAttributes(cart, desired) {
    const updates = {};
    for (const key of Object.keys(desired)) {
      if (String(cart.attributes[key] || '') !== String(desired[key])) updates[key] = String(desired[key]);
    }
    if (!Object.keys(updates).length) return cart;
    return request('cart/update.js', { attributes: updates });
  }

  async function reconcile() {
    const cfg = configuration();
    if (!cfg) return;
    let changed = false;
    let cart = await request('cart.js');
    cart.attributes = cart.attributes || {};
    let state = localState(cart);
    let claim = storedClaim(cart.attributes, previewEnabled);
    if (pendingClaim) {
      // The gift stays the same across stages and lanes. Never downgrade the stored stage.
      claim = { ...pendingClaim, stage: Math.max(pendingClaim.stage, claim ? claim.stage : 0) };
      cart = await patchAttributes(cart, {
        skrt_recovery_offer: claim.offer,
        skrt_recovery_flow: claim.flow,
        skrt_recovery_stage: claim.stage,
        skrt_recovery_preview: '1'
      });
      pendingClaim = null;
      storageWrite(pendingKey, null);
    }
    state.recovery = Boolean(claim);
    if (manualDismissal !== null) {
      const intent = manualDismissal;
      state.dismissed = intent;
      cart = await patchAttributes(cart, { skrt_gift_opt_out: intent ? '1' : '0' });
      if (manualDismissal === intent) manualDismissal = null;
    }
    // A newer click can arrive while an attribute PATCH is in flight.
    // Honor that newest intent before deciding whether to add anything.
    state = { ...localState(cart), recovery: Boolean(claim) };
    storageWrite(storageKey, { cartToken: cart.token, dismissed: state.dismissed });
    lastCart = cart;
    try {
      for (let count = 0; count < 30; count++) {
        const next = planGift(cart, cfg, state);
        if (!next.action) break;
        if (next.action.type === 'add') {
          await request('cart/add.js', { items: [{ id: cfg.variantId, quantity: 1 }] });
        } else {
          await request('cart/change.js', { id: next.action.key, quantity: next.action.quantity });
        }
        changed = true;
        // Keys can change when Shopify recalculates discounts. Never reuse a stale key.
        cart = await request('cart.js');
        cart.attributes = cart.attributes || {};
        state = { ...localState(cart), recovery: Boolean(storedClaim(cart.attributes, previewEnabled)) };
        lastCart = cart;
      }
      const result = planGift(cart, cfg, state);
      if (result.action) throw new Error('Gift reconciliation limit reached');
      const qualification = result.recoveryEligible && result.publicEligible ? 'both' :
        result.recoveryEligible ? 'recovery' : result.publicEligible ? 'spend' : '';
      if (state.recovery || cart.attributes.skrt_gift_qualification) {
        cart = await patchAttributes(cart, {
          skrt_gift_qualification: qualification,
          skrt_recovery_gift_in_cart: result.giftQuantity === 1 ? '1' : '0'
        });
      }
      lastCart = cart;
      render(cart, cfg, state, false);
      // Local integration signal only. No Klaviyo/GA event is sent, and this is NOT redemption.
      const signal = JSON.stringify([cart.token, result.giftQuantity, qualification]);
      if (signal !== lastSignal) {
        lastSignal = signal;
        doc.dispatchEvent(new CustomEvent('skrt:gift-state', {
          detail: { quantity: result.giftQuantity, qualification, preview: state.recovery }
        }));
      }
    } catch (error) {
      // A lost POST response may still have changed the cart. Read before any subsequent retry.
      try { cart = await request('cart.js'); lastCart = cart; }
      catch (_) { /* Keep the last known cart, but never announce success. */ }
      render(cart, cfg, state, true);
      throw error;
    } finally {
      if (changed) {
        // Cart-page HTML is server-rendered. Only reload after a verified mutation.
        const cartPath = new URL(route('cart')).pathname;
        if (root.location.pathname.replace(/\/$/, '') === cartPath.replace(/\/$/, '')) {
          root.location.reload();
        } else {
          doc.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        }
      }
    }
  }

  function synchronize() {
    queued = true;
    if (running) return running;
    running = (async () => {
      try {
        while (queued) {
          queued = false;
          if (root.navigator.locks) await root.navigator.locks.request('skrt-shared-gift', reconcile);
          else await reconcile();
        }
      } catch (_) {
        const cfg = configuration();
        if (cfg) {
          const cart = lastCart || { items: [], total_price: 0, attributes: {} };
          render(cart, cfg, { ...localState(cart), recovery: Boolean(storedClaim(cart.attributes || {}, previewEnabled)) }, true);
        }
        queued = false; // Wait for a real cart update or explicit retry; do not hammer the cart.
      } finally { running = null; }
    })();
    return running;
  }

  function schedule() {
    root.clearTimeout(timer);
    timer = root.setTimeout(synchronize, 150);
  }

  function isGiftKey(key) {
    const cfg = configuration();
    if (!cfg || !key) return false;
    return String(key).split(':')[0] === String(cfg.variantId);
  }

  doc.addEventListener('click', (event) => {
    const target = event.target.closest && event.target.closest('[is="cart-remove-button"], [data-skrt-gift-restore]');
    if (!target) return;
    if (target.hasAttribute('data-skrt-gift-restore')) {
      manualDismissal = false;
      if (lastCart) storageWrite(storageKey, { cartToken: lastCart.token, dismissed: false });
      schedule();
    } else if (isGiftKey(target.getAttribute('data-index'))) {
      manualDismissal = true;
      if (lastCart) storageWrite(storageKey, { cartToken: lastCart.token, dismissed: true });
      schedule();
    }
  }, true);

  doc.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches && target.matches('input[name="updates[]"]') &&
        isGiftKey(target.getAttribute('data-index')) && Number(target.value) === 0) {
      manualDismissal = true;
      if (lastCart) storageWrite(storageKey, { cartToken: lastCart.token, dismissed: true });
      schedule();
    }
  }, true);

  for (const type of ['cart:updated', 'ajaxProduct:added', 'cart:refresh', 'shopify:section:load']) {
    doc.addEventListener(type, schedule);
  }
  doc.addEventListener('visibilitychange', () => { if (!doc.hidden) schedule(); });
  // Concept loads the drawer asynchronously; start when its shared module actually mounts.
  const observer = new MutationObserver((records) => {
    if (records.some((record) => Array.from(record.addedNodes).some((node) =>
      node.nodeType === 1 && (node.matches('[data-skrt-gwp]') || node.querySelector('[data-skrt-gwp]'))
    ))) schedule();
  });
  observer.observe(doc.documentElement, { childList: true, subtree: true });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})(typeof window === 'undefined' ? null : window);
